import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { errorMessage } from "../../shared/api/errors.ts";
import { deviceName, type Device } from "../../shared/devices/devices.ts";
import {
  authorName,
  cardText,
  messageDeliveryStatus,
  messageActions,
  relativeTime,
  useDeleteMessage,
  useFeed,
  usePinMessage,
  useReplayMessage,
  type FeedFilter,
  type Message,
} from "../../shared/messages/messages.ts";
import { MessageImage } from "../../shared/messages/MessageImage.tsx";
import { useSession } from "../../shared/session/SessionProvider.tsx";
import { Avatar } from "../../shared/ui/Avatar/Avatar.tsx";
import { Button } from "../../shared/ui/Button/Button.tsx";
import { MoreIcon, PinIcon } from "../../shared/ui/icons.tsx";
import { Menu, type MenuItem } from "../../shared/ui/Menu/Menu.tsx";
import { Spinner } from "../../shared/ui/Spinner/Spinner.tsx";
import { StatusBadge } from "../../shared/ui/StatusBadge/StatusBadge.tsx";
import styles from "./Feed.module.css";

/** На главной — последние сообщения; остальное во «Всей истории». */
const HOME_FEED_LIMIT = 10;

const filters: { value: FeedFilter; label: string; empty: string }[] = [
  { value: "all", label: "Все", empty: "Сообщений пока нет." },
  { value: "incoming", label: "Входящие", empty: "Входящих сообщений пока нет." },
  { value: "outgoing", label: "Отправленные", empty: "Отправленных сообщений пока нет." },
  { value: "pinned", label: "Закреплённые", empty: "Закреплённых сообщений пока нет." },
];

/** Feed / Filter (Figma 287:1697): фильтры и карточки последних сообщений со всех E-va. */
export function Feed({ devices }: { devices: Device[] }) {
  const [filter, setFilter] = useState<FeedFilter>("all");
  const feed = useFeed(filter, HOME_FEED_LIMIT);

  return (
    <section className={styles.feed}>
      <FeedFilters value={filter} onChange={setFilter} />
      {feed.isPending ? (
        <div className={styles.placeholder} role="status" aria-label="Загрузка сообщений">
          <Spinner size={48} />
        </div>
      ) : feed.isError ? (
        <div className={styles.placeholder}>
          <p className={styles.notice}>Не удалось загрузить сообщения.</p>
          <Button variant="secondary" onClick={() => void feed.refetch()}>
            Повторить
          </Button>
        </div>
      ) : feed.data.length === 0 ? (
        <div className={styles.placeholder}>
          <p className={styles.notice}>{emptyFeedText(filter)}</p>
        </div>
      ) : (
        <>
          <ul className={styles.messages} aria-label="Лента">
            {feed.data.map((message) => (
              <li key={message.id}>
                <MessageCard
                  message={message}
                  device={devices.find((item) => item.id === message.deviceId)}
                  detailsPath={`/messages/${message.id}`}
                />
              </li>
            ))}
          </ul>
          <Link to="/history" className={styles.history}>
            Вся история
          </Link>
        </>
      )}
    </section>
  );
}

/** Пустая лента — свой текст на каждый фильтр. */
export function emptyFeedText(filter: FeedFilter): string | undefined {
  return filters.find((item) => item.value === filter)?.empty;
}

/** Filter / Actions (Figma 627:4893): «Все / Входящие / Отправленные / Закреплённые». */
export function FeedFilters({ value, onChange }: { value: FeedFilter; onChange: (filter: FeedFilter) => void }) {
  return (
    <div className={styles.filters} role="group" aria-label="Фильтр ленты">
      {filters.map((item) => (
        <button
          key={item.value}
          type="button"
          className={styles.filter}
          aria-pressed={item.value === value}
          onClick={() => onChange(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

type MessageCardProps = {
  message: Message;
  device: Device | undefined;
  /** Шторка «Информация о сообщении» — вложенный маршрут экрана, где лежит карточка. */
  detailsPath: string;
};

/**
 * Карточка сообщения (Figma 610:2448, с картинкой и подписью — 610:2461, с меню «…» — 627:4903). Тап по карточке открывает детали,
 * в меню — детали и действия, которые разрешены смотрящему.
 */
export function MessageCard({ message, device, detailsPath }: MessageCardProps) {
  const { user } = useSession();
  const { sender } = message;
  const status = messageDeliveryStatus(message);
  const actions = useCardActions(message, device, detailsPath);
  const text = cardText(message);
  return (
    <article className={styles.card}>
      <header className={styles.info}>
        <Avatar userId={sender.id} name={sender.displayName} />
        <div className={styles.author}>
          <p className={styles.name}>{authorName(message, user?.id)}</p>
          <p className={styles.recipient}>на {device ? deviceName(device) : "E-va"}</p>
        </div>
        <Menu
          label="Действия с сообщением"
          trigger={<MoreIcon />}
          items={actions.items}
          disabled={actions.pending}
          className={styles.menu}
        />
      </header>
      {/* Ссылка растянута на всю карточку (::after), кнопка меню лежит над ней. */}
      <Link to={detailsPath} className={styles.content}>
        <MessageImage className={styles.media} message={message} loading="lazy" />
        {text && <span className={styles.text}>{text}</span>}
      </Link>
      <footer className={styles.footer}>
        <div className={styles.badges}>
          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
          {(message.likeCount ?? 0) > 0 && <StatusBadge tone="success">♥ {message.likeCount}</StatusBadge>}
          {message.pinned && (
            <StatusBadge tone="muted" icon={<PinIcon />}>
              Закреплено
            </StatusBadge>
          )}
        </div>
        <time className={styles.time} dateTime={message.createdAt}>
          {relativeTime(message.createdAt)}
        </time>
      </footer>
      {actions.error && (
        <p className={styles.error} role="alert">
          {errorMessage(actions.error)}
        </p>
      )}
    </article>
  );
}

/** Пункты меню «…»: недоступные смотрящему действия не показываются вовсе. */
function useCardActions(message: Message, device: Device | undefined, detailsPath: string) {
  const { user } = useSession();
  const navigate = useNavigate();
  const pin = usePinMessage(message.id);
  const replay = useReplayMessage(message.id);
  const remove = useDeleteMessage(message.id);
  const mutations = [pin, replay, remove];
  const allowed = messageActions(message, device, user?.id);

  // У карточки видна только ошибка последнего действия.
  function run(action: () => void) {
    for (const mutation of mutations) mutation.reset();
    action();
  }

  const items: MenuItem[] = [{ label: "Информация о сообщении", onSelect: () => void navigate(detailsPath) }];
  if (allowed.pin) {
    items.push({
      label: message.pinned ? "Открепить" : "Закрепить",
      onSelect: () => run(() => pin.mutate(!message.pinned)),
    });
  }
  if (allowed.replay) items.push({ label: "Показать снова", onSelect: () => run(() => replay.mutate()) });
  if (allowed.delete) items.push({ label: "Удалить", tone: "danger", onSelect: () => run(() => remove.mutate()) });

  return {
    items,
    pending: mutations.some((mutation) => mutation.isPending),
    error: mutations.find((mutation) => mutation.error)?.error ?? null,
  };
}
